import { useEffect, useMemo, useState } from "react";
import { BookDetailCard } from "@web/components/features/shelf/book-detail-card";
import { BookDetailPickerPanel } from "@web/components/features/shelf/book-detail-picker-panel";
import { getSourceBookDetail, type BookshelfViewItem, type SourceBookDetail, type SourceManifest } from "../api";
import { getBookshelfGroupId, getSelectedBookSource } from "../reader/reader-library";
import {
  createDisplayChapters,
  createDisplaySourceOptions,
  getCurrentChapterLabel,
  getSourceName,
  mergeBookDetailIntoItem,
} from "./book-detail-data";
import type { BookDetailPanel } from "./types";

export interface BookDetailDialogProps {
  item: BookshelfViewItem;
  sources: SourceManifest[];
  selectedSourceByBook: Record<string, string>;
  onClose: () => void;
  onOpen: (chapterId?: string) => void;
  onSourceChange: (sourceId: string) => void;
  onSecondaryAction: () => Promise<void>;
  onMoveGroup: (groupId: string) => Promise<void>;
  secondaryActionLabel?: string;
  secondaryActionVariant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
}

export function BookDetailDialog({
  item,
  sources,
  selectedSourceByBook,
  onClose,
  onOpen,
  onSourceChange,
  onSecondaryAction,
  onMoveGroup,
  secondaryActionLabel,
  secondaryActionVariant,
}: BookDetailDialogProps) {
  const [groupId, setGroupId] = useState(getBookshelfGroupId(item));
  const [activePanel, setActivePanel] = useState<BookDetailPanel>(null);
  const [busy, setBusy] = useState(false);
  const [bookDetail, setBookDetail] = useState<SourceBookDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const selectedSource = getSelectedBookSource(item.book.id, item.sources, selectedSourceByBook, item.item.activeSourceId);
  const detailItem = useMemo(() => mergeBookDetailIntoItem(item, bookDetail, selectedSource?.sourceId), [item, bookDetail, selectedSource?.sourceId]);
  const detailSelectedSource = getSelectedBookSource(detailItem.book.id, detailItem.sources, selectedSourceByBook, detailItem.item.activeSourceId);
  const sourceOptions = useMemo(() => createDisplaySourceOptions(detailItem, sources), [detailItem, sources]);
  const displayCatalogChapters = useMemo(
    () => (bookDetail?.chapters?.length ? bookDetail.chapters : createDisplayChapters(detailItem)),
    [bookDetail?.chapters, detailItem],
  );
  const selectedSourceName = selectedSource ? getSourceName(sources, selectedSource.sourceId) : "未知书源";
  const latestChapterLabel = detailSelectedSource?.latestChapterTitle ?? bookDetail?.latestChapterTitle ?? "暂无最新章节";
  const catalogEntryLabel =
    detailItem.progress
      ? getCurrentChapterLabel(displayCatalogChapters, detailItem.progress.chapterId, detailItem.progress.chapterIndex) ??
        `第 ${detailItem.progress.chapterIndex + 1} 章`
      : displayCatalogChapters[0]?.title ?? (detailLoading ? "正在加载目录" : "暂无目录");

  useEffect(() => {
    // 切换书源后收起二级面板 避免旧书源状态残留
    setActivePanel(null);
  }, [selectedSource?.sourceId, selectedSource?.sourceBookId]);

  useEffect(() => {
    if (!selectedSource) {
      setBookDetail(null);
      setDetailError(null);
      setDetailLoading(false);
      return;
    }

    let ignored = false;
    setBookDetail(null);
    setDetailError(null);
    setDetailLoading(true);

    // 详情页展示依赖插件解析结果  搜索和书架都复用这里的加载流程
    getSourceBookDetail(selectedSource.sourceId, selectedSource.sourceBookId)
      .then(detail => {
        if (!ignored) setBookDetail(detail);
      })
      .catch(error => {
        if (!ignored) setDetailError(error instanceof Error ? error.message : String(error));
      })
      .finally(() => {
        if (!ignored) setDetailLoading(false);
      });

    return () => {
      ignored = true;
    };
  }, [selectedSource?.sourceId, selectedSource?.sourceBookId]);

  async function handleGroupChange(nextGroupId: string) {
    setGroupId(nextGroupId);
    setActivePanel(null);
    setBusy(true);

    try {
      await onMoveGroup(nextGroupId);
    } finally {
      setBusy(false);
    }
  }

  function handleSourceChange(sourceId: string) {
    onSourceChange(sourceId);
    setActivePanel(null);
  }

  function handleCatalogToggle() {
    if (!selectedSource) return;
    setActivePanel(activePanel === "catalog" ? null : "catalog");
  }

  async function handleSecondaryAction() {
    setBusy(true);

    try {
      await onSecondaryAction();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/45 p-3 backdrop-blur-sm sm:items-center sm:justify-center" onClick={onClose}>
      <BookDetailCard
        item={detailItem}
        activePanel={activePanel}
        busy={busy}
        currentChapterLabel={catalogEntryLabel}
        detailError={detailError}
        detailLoading={detailLoading}
        groupId={groupId}
        selectedSourceName={selectedSourceName}
        selectedSourceLatestChapterAt={detailSelectedSource?.latestChapterAt}
        selectedSourceLatestChapter={detailSelectedSource?.latestChapterTitle}
        selectedSourceAvailable={Boolean(detailSelectedSource)}
        secondaryActionLabel={secondaryActionLabel}
        secondaryActionVariant={secondaryActionVariant}
        onOpen={() => onOpen()}
        onPanelChange={setActivePanel}
        onSecondaryAction={() => void handleSecondaryAction()}
        onCatalogToggle={handleCatalogToggle}
      />

      {activePanel && (
        <BookDetailPickerPanel
          panel={activePanel}
          catalogChapters={displayCatalogChapters}
          catalogError={detailError}
          catalogLoading={detailLoading}
          groupId={groupId}
          item={detailItem}
          sourceOptions={sourceOptions}
          selectedSource={detailSelectedSource}
          onChapterOpen={onOpen}
          onClose={() => setActivePanel(null)}
          onGroupChange={nextGroupId => void handleGroupChange(nextGroupId)}
          onSourceChange={handleSourceChange}
        />
      )}
    </div>
  );
}
